import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  getFacebookConfigAPI, 
  deleteFacebookConfigAPI, 
  updateFacebookConfigAPI, 
  getUniqueAdsAPI 
} from '../../api-wa/facebookApi';

const FacebookConfigDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const page = parseInt(searchParams.get('page')) || 1;

  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [uniqueAds, setUniqueAds] = useState([]);

  useEffect(() => {
    fetchConfigs();
    fetchUniqueAds();
  }, [page]);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const res = await getFacebookConfigAPI({ page });
      setConfigs(res.data || []);
      setTotalPages(res.pages || 1);
      setTotalCount(res.count || 0);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load Facebook configurations');
    } finally {
      setLoading(false);
    }
  };

  const fetchUniqueAds = async () => {
    try {
      const res = await getUniqueAdsAPI();
      setUniqueAds(res || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleToggleActive = async (config) => {
    const nextStatus = config.active_status === 'active' ? 'inactive' : 'active';
    try {
      await updateFacebookConfigAPI({
        ...config,
        active_status: nextStatus,
        // map id mapping
        _id: config.id
      });
      toast.success(`App status set to ${nextStatus}`);
      fetchConfigs();
    } catch (err) {
      console.error(err);
      toast.error('Failed to toggle App status');
    }
  };

  const handleDeleteConfig = async (id) => {
    if (window.confirm('Are you sure you want to delete this configuration?')) {
      try {
        await deleteFacebookConfigAPI(id);
        toast.success('Configuration deleted');
        fetchConfigs();
      } catch (err) {
        console.error(err);
        toast.error('Failed to delete configuration');
      }
    }
  };

  return (
    <div className="container-fluid p-4 poppins bg-gray-50 min-h-screen">
      <div className="flex items-center justify-between gap-4 mb-6">
        <h2 className="text-2xl font-bold text-gray-800 m-0">Facebook Ad Automation</h2>
        <button
          onClick={() => navigate('/facebook/configuration/form')}
          className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-4 py-2 rounded-xl shadow-sm transition-all text-sm"
        >
          Add New App
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <div className="space-y-6">
          {configs.map((config) => {
            const isActive = config.active_status === 'active';
            return (
              <div
                key={config.id}
                className={`bg-white rounded-2xl shadow-sm border-s-4 p-6 transition-all duration-300 ${
                  isActive ? 'border-indigo-600' : 'border-gray-300 opacity-80'
                }`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                      {config.app_name}
                      <span className="text-xs text-gray-400 font-medium">({config.app_id})</span>
                    </h3>
                    <div className="text-sm text-gray-500 mt-1">
                      <span className="font-semibold">Verify Token:</span> {config.verify_token}
                    </div>
                  </div>

                  {/* Actions (Toggle, Edit, Delete) */}
                  <div className="flex items-center gap-3">
                    {/* Toggle Active Switch */}
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isActive}
                        onChange={() => handleToggleActive(config)}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                    </label>

                    {/* Edit button */}
                    <button
                      onClick={() => navigate(`/facebook/configuration/form?id=${config.id}`)}
                      className="p-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg transition-all"
                      title="Edit"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>

                    {/* Delete button */}
                    <button
                      onClick={() => handleDeleteConfig(config.id)}
                      className="p-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-all"
                      title="Delete"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Pages Connected */}
                {config.pages?.length > 0 && (
                  <div className="mt-4 border-t pt-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Pages Connected ({config.pages.length}):</h4>
                    <div className="space-y-3">
                      {config.pages.map((page, pIdx) => {
                        return (
                          <div
                            key={pIdx}
                            className="bg-slate-50 p-4 rounded-xl border border-slate-200 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 text-xs"
                          >
                            <div className="space-y-1">
                              <p className="font-bold text-gray-800">
                                {page.page_name} <span className="text-gray-400">({page.page_id})</span>
                              </p>
                              <p className="text-gray-500 font-medium">
                                Token: <span className="font-mono">{page.page_access_token?.substring(0, 15)}...</span>
                              </p>
                              <div className="flex gap-4 text-[10px] text-gray-400 font-medium">
                                {page.generated_date && (
                                  <span>Generated: {new Date(page.generated_date).toLocaleDateString()}</span>
                                )}
                                {page.expiry_date && (
                                  <span>Expires: {new Date(page.expiry_date).toLocaleDateString()}</span>
                                )}
                                <span className="capitalize">Status: {page.active_status}</span>
                              </div>
                            </div>

                            {/* View Leads for Ad dropdown */}
                            <div className="min-w-[220px] w-full md:w-auto">
                              <span className="font-semibold text-gray-600 block mb-1 text-[11px]">View Leads for Ad:</span>
                              <select
                                onChange={(e) => {
                                  if (e.target.value) {
                                    // Redirect to leads page with ad filters
                                    navigate(`/activity/leads?subTab=facebook&adId=${e.target.value}`);
                                  }
                                }}
                                className="w-full bg-white border border-gray-200 rounded-lg px-2 py-1.5 outline-none shadow-sm transition-all focus:border-indigo-500"
                                defaultValue=""
                              >
                                <option value="">Select Ad...</option>
                                {uniqueAds.map((ad) => (
                                  <option key={ad.ad_id} value={ad.ad_id}>
                                    {ad.ad_name || ad.ad_id}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="mt-3 flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${isActive ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                  <span className="text-xs font-semibold capitalize text-gray-500">{config.active_status}</span>
                </div>
              </div>
            );
          })}

          {configs.length === 0 && (
            <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-gray-200 text-gray-500">
              No Facebook configuration found. Click "Add New App" to get started.
            </div>
          )}

          {/* Pagination */}
          {totalCount > 0 && (
            <div className="flex justify-between items-center bg-white p-4 rounded-xl shadow-sm border text-xs">
              <div className="font-semibold text-gray-600">Total: {totalCount}</div>
              <div className="flex gap-2">
                <button
                  disabled={page === 1}
                  onClick={() => navigate(`/facebook/configuration?page=${Math.max(page - 1, 1)}`)}
                  className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-lg disabled:opacity-50"
                >
                  Previous
                </button>
                <div className="px-3 py-1 bg-indigo-600 text-white font-semibold rounded-lg">
                  {page} of {totalPages}
                </div>
                <button
                  disabled={page === totalPages}
                  onClick={() => navigate(`/facebook/configuration?page=${Math.min(page + 1, totalPages)}`)}
                  className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-lg disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FacebookConfigDashboard;
